import jsPDF from 'jspdf';

export interface ReportOptions {
  includeClient: boolean;
  includePersonnes: boolean;
  includeLieux: boolean;
  includeNotes: boolean;
  includePhotos: boolean;
  includeFichiers: boolean;
  includeTimeline: boolean;
  includeStats: boolean;
  includeObjectifs: boolean;
  includeElementsFournis: boolean;
  includeSommaire: boolean;
  includeConclusions: boolean;
}

export class ReportService {
  static async generatePDF(enquete: any, options: ReportOptions = {
    includeClient: true,
    includePersonnes: true,
    includeLieux: true,
    includeNotes: true,
    includePhotos: true,
    includeFichiers: true,
    includeTimeline: true,
    includeStats: true,
    includeObjectifs: true,
    includeElementsFournis: true,
    includeSommaire: true,
    includeConclusions: true
  }): Promise<void> {
    const doc = new jsPDF();
    let yPosition = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);

    // En-tête avec logo et titre
    doc.setFillColor(255, 140, 0); // Orange
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setFontSize(28);
    doc.setTextColor(255, 255, 255);
    doc.text('RAPPORT D\'ENQUÊTE', pageWidth / 2, 25, { align: 'center' });
    
    yPosition = 50;

    // Informations de l'enquête dans une boîte
    doc.setFillColor(50, 50, 50);
    doc.rect(margin, yPosition, contentWidth, 60, 'F');
    
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.text(`Enquête: ${enquete.titre}`, margin + 10, yPosition + 15);
    
    doc.setFontSize(12);
    doc.setTextColor(200, 200, 200);
    doc.text(`Statut: ${enquete.statut.replace('_', ' ')}`, margin + 10, yPosition + 30);
    doc.text(`Priorité: ${enquete.priorite}`, margin + 10, yPosition + 42);
    doc.text(`Client: ${enquete.client}`, margin + 10, yPosition + 54);
    
    yPosition += 80;

    // Description
    if (enquete.description) {
      doc.setFontSize(16);
      doc.setTextColor(255, 140, 0);
      doc.text('DESCRIPTION', margin, yPosition);
      yPosition += 15;

      doc.setFontSize(11);
      doc.setTextColor(200, 200, 200);
      const descriptionLines = this.splitTextToSize(enquete.description, contentWidth, doc);
      descriptionLines.forEach(line => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, margin, yPosition);
        yPosition += 7;
      });
      yPosition += 15;
    }

    // Sommaire
    if (options.includeSommaire) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(16);
      doc.setTextColor(255, 140, 0);
      doc.text('SOMMAIRE', margin, yPosition);
      yPosition += 15;

      doc.setFontSize(11);
      doc.setTextColor(200, 200, 200);
      const sommaireLines = this.splitTextToSize(
        `Ce rapport présente les résultats de l'enquête "${enquete.titre}" menée pour le compte de ${enquete.client}. ` +
        `L'investigation a permis d'identifier ${enquete.personnes?.length || 0} personnes d'intérêt et ${enquete.lieux?.length || 0} lieux pertinents. ` +
        `Le rapport détaille les objectifs, la méthodologie, les découvertes et les conclusions de cette enquête.`,
        contentWidth, doc
      );
      sommaireLines.forEach(line => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, margin, yPosition);
        yPosition += 7;
      });
      yPosition += 15;
    }

    // Objectifs de l'enquête
    if (options.includeObjectifs && enquete.objectifs) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(16);
      doc.setTextColor(255, 140, 0);
      doc.text('OBJECTIFS DE L\'ENQUÊTE', margin, yPosition);
      yPosition += 15;

      doc.setFontSize(11);
      doc.setTextColor(200, 200, 200);
      const objectifsLines = this.splitTextToSize(enquete.objectifs, contentWidth, doc);
      objectifsLines.forEach(line => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, margin, yPosition);
        yPosition += 7;
      });
      yPosition += 15;
    }

    // Éléments fournis par le client
    if (options.includeElementsFournis && enquete.elementsFournis) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(16);
      doc.setTextColor(255, 140, 0);
      doc.text('ÉLÉMENTS FOURNIS PAR LE CLIENT', margin, yPosition);
      yPosition += 15;

      doc.setFontSize(11);
      doc.setTextColor(200, 200, 200);
      const elementsLines = this.splitTextToSize(enquete.elementsFournis, contentWidth, doc);
      elementsLines.forEach(line => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, margin, yPosition);
        yPosition += 7;
      });
      yPosition += 15;
    }

    // Statistiques
    if (options.includeStats) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(16);
      doc.setTextColor(255, 140, 0);
      doc.text('STATISTIQUES', margin, yPosition);
      yPosition += 15;

      // Boîte pour les statistiques
      doc.setFillColor(50, 50, 50);
      doc.rect(margin, yPosition, contentWidth, 50, 'F');
      
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.text(`Personnes d'intérêt: ${enquete.personnes?.length || 0}`, margin + 10, yPosition + 15);
      doc.text(`Lieux identifiés: ${enquete.lieux?.length || 0}`, margin + 10, yPosition + 27);
      doc.text(`Notes prises: ${enquete.notes?.length || 0}`, margin + 10, yPosition + 39);
      
      yPosition += 70;
    }

    // Informations client
    if (options.includeClient && enquete.infosClient) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text('Informations Client:', margin, yPosition);
      yPosition += 10;

      doc.setFontSize(10);
      doc.setTextColor(200, 200, 200);
      doc.text(`Nom: ${enquete.infosClient.nom}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Email: ${enquete.infosClient.email}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Téléphone: ${enquete.infosClient.telephone}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Adresse: ${enquete.infosClient.adresse}`, margin, yPosition);
      yPosition += 6;
      if (enquete.infosClient.notes) {
        doc.text(`Notes: ${enquete.infosClient.notes}`, margin, yPosition);
        yPosition += 6;
      }
      yPosition += 10;
    }

    // Personnes d'intérêt
    if (options.includePersonnes && enquete.personnes?.length > 0) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(16);
      doc.setTextColor(255, 140, 0);
      doc.text('PERSONNES D\'INTÉRÊT', margin, yPosition);
      yPosition += 15;

      enquete.personnes.forEach((personne: any, index: number) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        // Boîte pour chaque personne
        doc.setFillColor(50, 50, 50);
        doc.rect(margin, yPosition, contentWidth, 60, 'F');
        
        doc.setFontSize(14);
        doc.setTextColor(255, 255, 255);
        doc.text(`${index + 1}. ${personne.nom}`, margin + 10, yPosition + 15);
        
        doc.setFontSize(10);
        doc.setTextColor(200, 200, 200);
        if (personne.email) {
          doc.text(`Email: ${personne.email}`, margin + 10, yPosition + 27);
        }
        if (personne.telephone) {
          doc.text(`Téléphone: ${personne.telephone}`, margin + 10, yPosition + 39);
        }
        if (personne.statut) {
          doc.text(`Statut: ${personne.statut}`, margin + 10, yPosition + 51);
        }
        
        yPosition += 80;
      });
    }

    // Lieux
    if (options.includeLieux && enquete.lieux?.length > 0) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text('Lieux:', margin, yPosition);
      yPosition += 10;

      enquete.lieux.forEach((lieu: any, index: number) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(12);
        doc.setTextColor(255, 140, 0);
        doc.text(`${index + 1}. ${lieu.nom}`, margin, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(200, 200, 200);
        if (lieu.adresse) {
          doc.text(`Adresse: ${lieu.adresse}`, margin + 10, yPosition);
          yPosition += 6;
        }
        if (lieu.description) {
          doc.text(`Description: ${lieu.description}`, margin + 10, yPosition);
          yPosition += 6;
        }
        doc.text(`Type: ${lieu.type}`, margin + 10, yPosition);
        yPosition += 6;
        if (lieu.coordonnees) {
          doc.text(`Coordonnées: ${lieu.coordonnees.lat}, ${lieu.coordonnees.lng}`, margin + 10, yPosition);
          yPosition += 6;
        }
        yPosition += 5;
      });
    }

    // Notes
    if (options.includeNotes && enquete.notes?.length > 0) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text('Notes:', margin, yPosition);
      yPosition += 10;

      enquete.notes.forEach((note: any, index: number) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(12);
        doc.setTextColor(255, 140, 0);
        doc.text(`${index + 1}. ${note.titre}`, margin, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(200, 200, 200);
        doc.text(`Date: ${note.date}`, margin + 10, yPosition);
        yPosition += 6;
        doc.text(`Auteur: ${note.auteur}`, margin + 10, yPosition);
        yPosition += 6;
        doc.text(`Priorité: ${note.priorite}`, margin + 10, yPosition);
        yPosition += 6;
        if (note.contenu) {
          const contentLines = this.splitTextToSize(note.contenu, contentWidth - 10, doc);
          contentLines.forEach(line => {
            if (yPosition > 250) {
              doc.addPage();
              yPosition = 20;
            }
            doc.text(line, margin + 10, yPosition);
            yPosition += 6;
          });
        }
        if (note.tags?.length > 0) {
          doc.text(`Tags: ${note.tags.join(', ')}`, margin + 10, yPosition);
          yPosition += 6;
        }
        yPosition += 5;
      });
    }

    // Photos
    if (options.includePhotos && enquete.photos?.length > 0) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text('Photos:', margin, yPosition);
      yPosition += 10;

      enquete.photos.forEach((photo: any, index: number) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(12);
        doc.setTextColor(255, 140, 0);
        doc.text(`${index + 1}. ${photo.nom}`, margin, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(200, 200, 200);
        if (photo.description) {
          doc.text(`Description: ${photo.description}`, margin + 10, yPosition);
          yPosition += 6;
        }
        doc.text(`Date: ${photo.date}`, margin + 10, yPosition);
        yPosition += 6;
        if (photo.lieu) {
          doc.text(`Lieu: ${photo.lieu}`, margin + 10, yPosition);
          yPosition += 6;
        }
        if (photo.tags?.length > 0) {
          doc.text(`Tags: ${photo.tags.join(', ')}`, margin + 10, yPosition);
          yPosition += 6;
        }
        yPosition += 5;
      });
    }

    // Fichiers
    if (options.includeFichiers && enquete.fichiers?.length > 0) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text('Fichiers:', margin, yPosition);
      yPosition += 10;

      enquete.fichiers.forEach((fichier: any, index: number) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(12);
        doc.setTextColor(255, 140, 0);
        doc.text(`${index + 1}. ${fichier.nom}`, margin, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(200, 200, 200);
        doc.text(`Type: ${fichier.type}`, margin + 10, yPosition);
        yPosition += 6;
        doc.text(`Taille: ${fichier.taille}`, margin + 10, yPosition);
        yPosition += 6;
        doc.text(`Date: ${fichier.date}`, margin + 10, yPosition);
        yPosition += 6;
        if (fichier.description) {
          doc.text(`Description: ${fichier.description}`, margin + 10, yPosition);
          yPosition += 6;
        }
        yPosition += 5;
      });
    }

    // Timeline
    if (options.includeTimeline && enquete.timeline?.length > 0) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text('Timeline:', margin, yPosition);
      yPosition += 10;

      enquete.timeline.forEach((item: any, index: number) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(12);
        doc.setTextColor(255, 140, 0);
        doc.text(`${index + 1}. ${item.titre}`, margin, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(200, 200, 200);
        doc.text(`Date: ${item.date}`, margin + 10, yPosition);
        yPosition += 6;
        doc.text(`Type: ${item.type}`, margin + 10, yPosition);
        yPosition += 6;
        doc.text(`Auteur: ${item.auteur}`, margin + 10, yPosition);
        yPosition += 6;
        if (item.description) {
          doc.text(`Description: ${item.description}`, margin + 10, yPosition);
          yPosition += 6;
        }
        yPosition += 5;
      });
    }

    // Conclusions
    if (options.includeConclusions) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(16);
      doc.setTextColor(255, 140, 0);
      doc.text('CONCLUSIONS', margin, yPosition);
      yPosition += 15;

      doc.setFontSize(11);
      doc.setTextColor(200, 200, 200);
      
      // Générer des conclusions basées sur les données de l'enquête
      let conclusions = `L'enquête "${enquete.titre}" a été menée avec succès selon les objectifs définis. `;
      
      if (enquete.personnes?.length > 0) {
        conclusions += `L'investigation a permis d'identifier ${enquete.personnes.length} personne(s) d'intérêt. `;
      }
      
      if (enquete.lieux?.length > 0) {
        conclusions += `${enquete.lieux.length} lieu(x) pertinent(s) ont été cartographié(s). `;
      }
      
      if (enquete.notes?.length > 0) {
        conclusions += `${enquete.notes.length} note(s) d'investigation ont été prises. `;
      }
      
      conclusions += `Le rapport détaillé ci-dessus présente l'ensemble des découvertes et analyses réalisées dans le cadre de cette enquête. `;
      
      if (enquete.statut === 'terminee') {
        conclusions += `L'enquête est maintenant terminée et les résultats sont disponibles pour le client.`;
      } else {
        conclusions += `L'enquête est actuellement en cours et sera mise à jour au fur et à mesure des nouvelles découvertes.`;
      }

      const conclusionsLines = this.splitTextToSize(conclusions, contentWidth, doc);
      conclusionsLines.forEach(line => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, margin, yPosition);
        yPosition += 7;
      });
      yPosition += 15;
    }

    // Pied de page
    const pageCount = (doc as any).getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} sur ${pageCount}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
      doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, margin, doc.internal.pageSize.getHeight() - 10);
    }

    // Sauvegarder le PDF
    const fileName = `rapport_enquete_${enquete.titre.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  }

  private static splitTextToSize(text: string, maxWidth: number, doc: jsPDF): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine + word + ' ';
      const testWidth = doc.getTextWidth(testLine);
      
      if (testWidth > maxWidth && currentLine !== '') {
        lines.push(currentLine.trim());
        currentLine = word + ' ';
      } else {
        currentLine = testLine;
      }
    });

    if (currentLine.trim()) {
      lines.push(currentLine.trim());
    }

    return lines;
  }
} 